/**
 * Centralized constants for claims-related data.
 *
 * After spec 028 (Portal UI Migration to Encounter Service), this module
 * speaks the encounter-service vocabulary directly:
 *  - Backend `billType` is a code (OFFICE_VISIT, SURGICAL, …) — no display-label
 *    translation needed. CLAIM_TYPE maps code → label.
 *  - Backend `status` is the RAW backend workflow status (e.g. 'In Coding',
 *    'Ready to Bill'), mirroring the SharePoint Status field. encounter-service
 *    persists and returns it verbatim — the read-sync writes the raw value and the
 *    backend stays agnostic to the display vocabulary. The portal maps raw → display:
 *      * `mapToDisplayStatus` for rendering (StatusBadge),
 *      * `mapDisplayStatusToBackend` to expand a display filter into the set
 *        of raw statuses sent on `?status=` (encounter-service matches ANY via a
 *        comma-separated list).
 *  - The BILL_TYPE_TEXT_TO_CODE bridge and the deprecated CLAIM_STATUS alias
 *    remain gone (those were unrelated to status display).
 *
 * NOTE: this restores the SharePoint→display status translation that spec 028
 * had removed — the canonical store now holds the raw SharePoint status again,
 * so the mapping lives in the UI rather than the backend.
 *
 * Source of truth for the raw values: SharePointTicketService.cs (Status field).
 */

// ============================================================================
// CLAIM STATUSES (display vocabulary, sent verbatim to ?status=)
// ============================================================================

/** Display status values shown in the UI and sent on the wire. */
export const DISPLAY_STATUS = {
  PENDING: 'Pending',
  INCOMPLETE: 'Incomplete',
  PROCESSING: 'Processing',
  COMPLETED: 'Completed',
  CANCELED: 'Canceled',
} as const;

export type DisplayStatusValue = (typeof DISPLAY_STATUS)[keyof typeof DISPLAY_STATUS];

/** Filter dropdown options. The selected display value is expanded to the raw
 *  backend statuses via `mapDisplayStatusToBackend` before hitting ?status=. */
export const STATUS_OPTIONS: readonly { label: string; value: string }[] = [
  { label: 'All Statuses', value: '' },
  { label: 'Pending', value: DISPLAY_STATUS.PENDING },
  { label: 'Incomplete', value: DISPLAY_STATUS.INCOMPLETE },
  { label: 'Processing', value: DISPLAY_STATUS.PROCESSING },
  { label: 'Completed', value: DISPLAY_STATUS.COMPLETED },
  { label: 'Canceled', value: DISPLAY_STATUS.CANCELED },
] as const;

/** Raw backend workflow status values (mirror the SharePoint Status field values). */
export const BACKEND_STATUS = {
  NEW: 'New',
  PENDING_INFORMATION: 'Pending Information',
  MISSING_INFORMATION: 'Missing Information',
  GAP_HOLD: 'GAP Hold',
  IN_CODING: 'In Coding',
  SCA_CODING_REVIEW: 'SCA Coding Review',
  READY_FOR_IMAGINE: 'Ready for Imagine',
  READY_TO_BILL: 'Ready to Bill',
  COMPLETED: 'Completed',
  REJECTED_CLOSED: 'Rejected & Closed',
} as const;

export type BackendStatusValue = (typeof BACKEND_STATUS)[keyof typeof BACKEND_STATUS];

/**
 * Maps raw backend status values to display status values.
 * Unknown statuses are returned as-is (so already-display values like 'Pending'
 * and the uploads 'Complete' badge pass through unchanged).
 */
export const BACKEND_TO_DISPLAY_STATUS: Record<string, DisplayStatusValue> = {
  [BACKEND_STATUS.NEW]: DISPLAY_STATUS.PENDING,
  [BACKEND_STATUS.PENDING_INFORMATION]: DISPLAY_STATUS.PENDING,
  [BACKEND_STATUS.MISSING_INFORMATION]: DISPLAY_STATUS.INCOMPLETE,
  [BACKEND_STATUS.GAP_HOLD]: DISPLAY_STATUS.PROCESSING,
  [BACKEND_STATUS.IN_CODING]: DISPLAY_STATUS.PROCESSING,
  [BACKEND_STATUS.SCA_CODING_REVIEW]: DISPLAY_STATUS.PROCESSING,
  [BACKEND_STATUS.READY_FOR_IMAGINE]: DISPLAY_STATUS.PROCESSING,
  [BACKEND_STATUS.READY_TO_BILL]: DISPLAY_STATUS.PROCESSING,
  [BACKEND_STATUS.COMPLETED]: DISPLAY_STATUS.COMPLETED,
  [BACKEND_STATUS.REJECTED_CLOSED]: DISPLAY_STATUS.CANCELED,
};

/** Convert a raw backend status to a display status. */
export function mapToDisplayStatus(backendStatus: string): string {
  return BACKEND_TO_DISPLAY_STATUS[backendStatus] ?? backendStatus;
}

/**
 * Maps display status values back to the raw backend statuses they cover.
 * Used for filtering — selecting "Processing" must match every raw status that
 * collapses to Processing.
 */
// Each list also includes its own display value verbatim: mapToDisplayStatus() passes an
// unrecognized raw status through unchanged, so a row whose raw status IS ALREADY a display
// value (e.g. seeded/test data with a raw status of literally "Pending") renders under that
// display status — the filter must match it back, or that row becomes unreachable by filter.
export const DISPLAY_TO_BACKEND_STATUSES: Record<DisplayStatusValue, string[]> = {
  [DISPLAY_STATUS.PENDING]: [
    BACKEND_STATUS.NEW,
    BACKEND_STATUS.PENDING_INFORMATION,
    DISPLAY_STATUS.PENDING,
  ],
  [DISPLAY_STATUS.INCOMPLETE]: [
    BACKEND_STATUS.MISSING_INFORMATION,
    DISPLAY_STATUS.INCOMPLETE,
  ],
  [DISPLAY_STATUS.PROCESSING]: [
    BACKEND_STATUS.GAP_HOLD,
    BACKEND_STATUS.IN_CODING,
    BACKEND_STATUS.SCA_CODING_REVIEW,
    BACKEND_STATUS.READY_FOR_IMAGINE,
    BACKEND_STATUS.READY_TO_BILL,
    DISPLAY_STATUS.PROCESSING,
  ],
  [DISPLAY_STATUS.COMPLETED]: [
    BACKEND_STATUS.COMPLETED,
    DISPLAY_STATUS.COMPLETED,
  ],
  [DISPLAY_STATUS.CANCELED]: [
    BACKEND_STATUS.REJECTED_CLOSED,
    DISPLAY_STATUS.CANCELED,
  ],
};

/**
 * Convert display status filter value(s) to raw backend status value(s) for
 * the `?status=` query. Handles comma-separated lists of display statuses and
 * returns a comma-separated list of raw statuses (encounter-service matches ANY).
 */
export function mapDisplayStatusToBackend(displayStatuses: string): string {
  const displayList = displayStatuses.split(',').map((s) => s.trim()).filter(Boolean);
  const backendStatuses = new Set<string>();

  for (const display of displayList) {
    const mapped = DISPLAY_TO_BACKEND_STATUSES[display as DisplayStatusValue];
    if (mapped) {
      mapped.forEach((status) => backendStatuses.add(status));
    } else {
      // Unknown display status — pass through as-is.
      backendStatuses.add(display);
    }
  }

  return Array.from(backendStatuses).join(',');
}

// ============================================================================
// CLAIM TYPES (code → display label)
// ============================================================================

/**
 * Claim type / bill type vocabulary. Keys are wire codes (matching
 * encounter-service's shared `ClaimType` enum); values are display labels for
 * the claims list filter dropdown and the claims list status column.
 *
 * Submit / edit pages use their own per-feature provider config map
 * (`CLAIM_TYPE_PROVIDER_CONFIG` in submit-claim/providerConfig.ts) which keys
 * on the SAME codes — but carries richer metadata (provider role definitions)
 * and may use different labels suited to a card-style selection UI.
 */
export const CLAIM_TYPE = {
  OFFICE_VISIT:     'Office E/M',
  SURGICAL:         'Professional Surgical',
  HOSPITAL_CONSULT: 'Hospital Consult',
  ANESTHESIA:       'Anesthesia',
  PATHOLOGY:        'Pathology',
  NEUROMONITORING:  'Neuromonitoring',
  FACILITY:         'Facility UB04',
  ER_CONSULT:       'Consult + Procedure Same Admission',
  OLD_CLAIMS:       'Old Claims',
  IDR_ONLY:         'IDR Only',
  OFFICE_PROCEDURE: 'Office Procedure',
} as const;

export type ClaimTypeCode = keyof typeof CLAIM_TYPE;
export type ClaimTypeValue = (typeof CLAIM_TYPE)[ClaimTypeCode];

/** Filter dropdown options. `value` is the wire code; `label` is for display. */
export const CLAIM_TYPE_OPTIONS: readonly { label: string; value: string }[] = [
  { label: 'All Types', value: '' },
  ...(Object.entries(CLAIM_TYPE) as [ClaimTypeCode, string][]).map(
    ([code, label]) => ({ label, value: code }),
  ),
] as const;

// ============================================================================
// CLAIMS TABLE COLUMNS & SORTING (encounter-service sortBy keys)
// ============================================================================

/**
 * Column definitions for the claims table. `id` is the encounter-service sort
 * key sent on `?sortBy=`. CLAIM # is non-sortable — encounter-service has no
 * sort axis for case id (the table uses caseId as a tiebreaker, not a primary
 * sort key).
 */
export const CLAIM_COLUMNS = [
  { id: 'ticketId',         label: 'CLAIM #',     sortable: false, defaultDesc: false },
  { id: 'patientLastName',  label: 'PATIENT',     sortable: true,  defaultDesc: false },
  { id: 'provider',         label: 'PROVIDER',    sortable: false, defaultDesc: false },
  { id: 'escalation',       label: 'ESCALATION',  sortable: true,  defaultDesc: false },
  { id: 'dateOfService',    label: 'DOS',         sortable: true,  defaultDesc: true  },
  { id: 'billType',         label: 'CLAIM TYPE',  sortable: true,  defaultDesc: false },
  { id: 'createdAt',        label: 'CREATED',     sortable: true,  defaultDesc: true  },
  { id: 'status',           label: 'STATUS',      sortable: true,  defaultDesc: false },
] as const;

export type ClaimColumnDef = (typeof CLAIM_COLUMNS)[number];
export type ClaimSortKey = ClaimColumnDef['id'];

/** Sort keys derived from column definitions, for type-safe consumers. */
export const CLAIM_SORT_KEY = {
  TICKET_ID:         'ticketId',
  PATIENT_LAST_NAME: 'patientLastName',
  DATE_OF_SERVICE:   'dateOfService',
  BILL_TYPE:         'billType',
  CREATED_AT:        'createdAt',
  STATUS:            'status',
} as const satisfies Record<string, ClaimSortKey>;

/** Default sort column for the claims list (encounter-service sortBy key). */
export const DEFAULT_SORT_COLUMN: ClaimSortKey = 'createdAt';

/** Default sort direction (true = descending). */
export const DEFAULT_SORT_DESC = true;

// ============================================================================
// ESCALATION FILTER (feature 044) — shared by the portal claims list and the
// backoffice encounters queue so both segmented filters use the same union.
// ============================================================================

export type EscalationFilter = 'all' | 'escalated' | 'received' | 'awaiting';
